/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import DashboardRangeTabs from "@/components/dashboard/dashboard-range-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/utils";
import { fetchTableReports } from "@/redux/slice/dashboard/dashboard-slice";

const COLORS = {
  cyan: "#0EA5E9",
  emerald: "#10B981",
  purple: "#A855F7",
  royalBlue: "#3B82F6",
  gold: "#FBBF24",
};

const YEAR_COLORS = ["#3B82F6", "#FBBF24", "#10B981", "#A855F7"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomTooltip = ({ active, payload, label, currency = "GBP" }) => {
  if (active && payload && payload.length) {
    const value = Number(payload[0]?.value || 0);

    return (
      <div className="bg-[#1F2937] border border-white/10 px-4 py-2 rounded shadow-2xl flex flex-col items-center relative">
        <p className="text-white text-sm font-semibold">
          {formatCurrency(value, currency)}
        </p>
        <p className="text-gray-400 text-[10px]">{label}</p>
        <div className="absolute -bottom-1 w-2 h-2 bg-[#1F2937] rotate-45 border-r border-b border-white/10"></div>
      </div>
    );
  }
  return null;
};

const ChartSkeleton = ({ height }) => (
  <div className="w-full" style={{ height }}>
    <Skeleton className="h-full w-full" />
  </div>
);

const StatValue = ({ value, color, loading, currency }) => {
  if (loading) return <Skeleton className="h-10 w-24" />;

  return (
    <p className="text-3xl font-semibold" style={{ color }}>
      {currency
        ? formatCurrency(value || 0, currency)
        : Number(value || 0).toLocaleString()}
    </p>
  );
};

function TableReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1],
    [currentYear],
  );

  const recentMonths = useMemo(
    () =>
      [2, 1, 0].map((offset) => {
        const date = new Date(currentYear, currentMonth - 1 - offset, 1);
        return {
          value: date.getMonth() + 1,
          label: MONTH_NAMES[date.getMonth()],
        };
      }),
    [currentMonth, currentYear],
  );

  const [comparisonRange, setComparisonRange] = useState("weekly");
  const [comparisonYear, setComparisonYear] = useState(currentYear);
  const [comparisonMonth, setComparisonMonth] = useState(currentMonth);

  const [totalRevenueRange, setTotalRevenueRange] = useState("monthly");
  const [totalYear, setTotalYear] = useState(currentYear);
  const [totalMonth, setTotalMonth] = useState(currentMonth);

  const [compareYear1, setCompareYear1] = useState(currentYear - 1);
  const [compareYear2, setCompareYear2] = useState(currentYear);

  const hasLoadedInitialRef = useRef(false);

  const reportData = useSelector((state) => state.dashboard?.tableReports);
  const tableLoading = useSelector((state) => state.dashboard?.tableLoading);
  const restaurantId = user?.restaurant_id;

  const comparisonData = useMemo(
    () => reportData?.charts?.comparison?.[comparisonRange] || [],
    [reportData, comparisonRange],
  );

  const totalRevenueData = useMemo(
    () => reportData?.charts?.totalRevenue?.[totalRevenueRange] || [],
    [reportData, totalRevenueRange],
  );

  const yearlyComparisonData = useMemo(
    () => reportData?.charts?.yearlyComparison?.yearly || [],
    [reportData],
  );

  const yearlyKeys = useMemo(() => {
    const firstRow = yearlyComparisonData?.[0] || {};
    return Object.keys(firstRow)
      .filter((key) => key !== "label")
      .sort((a, b) => Number(a.replace("v", "")) - Number(b.replace("v", "")));
  }, [yearlyComparisonData]);

  const totalRevenueFromFilteredData = useMemo(
    () =>
      totalRevenueData.reduce(
        (acc, item) => acc + Number(item?.revenue || 0),
        0,
      ),
    [totalRevenueData],
  );

  const formatWeeklyAxisTick = (value) => {
    const numericValue = Number(value || 0);

    if (numericValue >= 1000) {
      const compactValue = numericValue / 1000;
      return `${Number.isInteger(compactValue) ? compactValue : compactValue.toFixed(1)}k`;
    }

    return `${numericValue}`;
  };

  const currency = user?.currency || "GBP";

  const isInitialLoading = Boolean(tableLoading?.initial);
  const isComparisonLoading =
    isInitialLoading || Boolean(tableLoading?.comparison);
  const isTotalLoading =
    isInitialLoading || Boolean(tableLoading?.totalRevenue);
  const isYearlyLoading =
    isInitialLoading || Boolean(tableLoading?.yearlyComparison);

  const requestComparisonData = (
    period,
    year = comparisonYear,
    month = comparisonMonth,
  ) => {
    const params =
      period === "monthly"
        ? { period: "monthly", year, month }
        : { period: "weekly" };

    dispatch(
      fetchTableReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "comparison" },
      }),
    );
  };

  const requestTotalRevenueData = (
    period,
    year = totalYear,
    month = totalMonth,
  ) => {
    const params =
      period === "yearly"
        ? { period: "yearly", year }
        : { period: "monthly", year, month };

    dispatch(
      fetchTableReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "totalRevenue" },
      }),
    );
  };

  const requestYearlyComparisonData = (
    year1 = compareYear1,
    year2 = compareYear2,
  ) => {
    dispatch(
      fetchTableReports({
        restaurant_id: restaurantId,
        params: {
          period: "yearly",
          compare_year1: year1,
          compare_year2: year2,
        },
        requestMeta: { chart: "yearlyComparison" },
      }),
    );
  };

  useEffect(() => {
    if (!restaurantId || hasLoadedInitialRef.current) return;

    dispatch(
      fetchTableReports({
        restaurant_id: restaurantId,
        params: { period: "weekly" },
        requestMeta: { mode: "initial" },
      }),
    );

    hasLoadedInitialRef.current = true;
  }, [dispatch, restaurantId]);

  const handleComparisonRangeChange = (value) => {
    setComparisonRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "weekly") {
      requestComparisonData("weekly");
      return;
    }

    requestComparisonData("monthly", comparisonYear, comparisonMonth);
  };

  const handleComparisonYearChange = (year) => {
    setComparisonYear(year);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", year, comparisonMonth);
  };

  const handleComparisonMonthChange = (month) => {
    setComparisonMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", comparisonYear, month);
  };

  const handleTotalRangeChange = (value) => {
    setTotalRevenueRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "yearly") {
      requestTotalRevenueData("yearly", totalYear);
      return;
    }

    requestTotalRevenueData("monthly", totalYear, totalMonth);
  };

  const handleTotalYearChange = (year) => {
    setTotalYear(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (totalRevenueRange === "yearly") {
      requestTotalRevenueData("yearly", year);
      return;
    }

    requestTotalRevenueData("monthly", year, totalMonth);
  };

  const handleTotalMonthChange = (month) => {
    setTotalMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      totalRevenueRange !== "monthly"
    )
      return;

    requestTotalRevenueData("monthly", totalYear, month);
  };

  const handleCompareYear1Change = (year) => {
    setCompareYear1(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    requestYearlyComparisonData(year, compareYear2);
  };

  const handleCompareYear2Change = (year) => {
    setCompareYear2(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    requestYearlyComparisonData(compareYear1, year);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Table Reports" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Table", path: "/dashboard/table-reports" },
      ]}
    >
      <div className="container mx-auto p-6 space-y-6 min-h-screen text-white">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-medium">Table</h1>
          <div className="flex gap-3 items-center">
            {comparisonRange === "monthly" && (
              <>
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={comparisonMonth}
                    onChange={(e) =>
                      handleComparisonMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={comparisonYear}
                    onChange={(e) =>
                      handleComparisonYearChange(Number(e.target.value))
                    }
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </>
            )}
            <DashboardRangeTabs
              options={[
                { label: "Weekly", value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
              value={comparisonRange}
              onChange={handleComparisonRangeChange}
              shape="full"
              className="border-white/10"
              buttonClassName="px-4 md:px-5 py-1.5 text-xs"
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 h-36 flex flex-col justify-between">
            <p className="text-gray-400 text-sm">Total Number of Table</p>
            <StatValue
              value={reportData?.stats?.totalTables || 0}
              color={COLORS.cyan}
              loading={isInitialLoading && !reportData}
            />
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 h-36 flex flex-col justify-between">
            <p className="text-gray-400 text-sm">Total Generated Revenue</p>
            <StatValue
              value={reportData?.stats?.generatedRevenue || 0}
              color={COLORS.emerald}
              loading={isInitialLoading && !reportData}
              currency={currency}
            />
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 h-36 flex flex-col justify-between">
            <p className="text-gray-400 text-sm">Total Avg Table Revenue</p>
            <StatValue
              value={reportData?.stats?.avgTableRevenue || 0}
              color={COLORS.purple}
              loading={isInitialLoading && !reportData}
              currency={currency}
            />
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 h-36 flex flex-col justify-between">
            <p className="text-gray-400 text-sm">Total No. of Guest Served</p>
            <StatValue
              value={reportData?.stats?.guestsServed || 0}
              color={COLORS.royalBlue}
              loading={isInitialLoading && !reportData}
            />
          </div>
        </div>

        <section className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-lg font-medium">
              {comparisonRange === "weekly" ? "Weekly" : "Monthly"} Comparison
            </h2>
            <div className="flex gap-6 text-[11px] font-medium text-gray-300">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" /> Total
                Number of Table
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" /> Total
                Generated Revenue
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#A855F7]" /> Total Avg.
                Table Revenue
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Total No
                of Guest Served
              </span>
            </div>
          </div>
          <div className="h-[420px] w-full">
            {isComparisonLoading ? (
              <ChartSkeleton height={420} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {comparisonRange === "weekly" ? (
                  <BarChart
                    data={comparisonData}
                    barCategoryGap="18%"
                    barGap={2}
                  >
                    <CartesianGrid
                      vertical={false}
                      stroke="#6B7280"
                      strokeOpacity={0.45}
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#F3F4F6", fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#F3F4F6", fontSize: 12 }}
                      tickFormatter={formatWeeklyAxisTick}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(255,255,255,0.03)" }}
                      content={<CustomTooltip currency={currency} />}
                    />
                    <Bar
                      dataKey="totalTables"
                      fill={COLORS.cyan}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="generatedRevenue"
                      fill={COLORS.emerald}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="avgTableRevenue"
                      fill={COLORS.purple}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="guestsServed"
                      fill={COLORS.royalBlue}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                  </BarChart>
                ) : (
                  <AreaChart data={comparisonData}>
                    <defs>
                      <linearGradient
                        id="gradTableCyan"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={COLORS.cyan}
                          stopOpacity={0.15}
                        />
                        <stop
                          offset="95%"
                          stopColor={COLORS.cyan}
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradTableGreen"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={COLORS.emerald}
                          stopOpacity={0.15}
                        />
                        <stop
                          offset="95%"
                          stopColor={COLORS.emerald}
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradTablePurple"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={COLORS.purple}
                          stopOpacity={0.15}
                        />
                        <stop
                          offset="95%"
                          stopColor={COLORS.purple}
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradTableBlue"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={COLORS.royalBlue}
                          stopOpacity={0.15}
                        />
                        <stop
                          offset="95%"
                          stopColor={COLORS.royalBlue}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      vertical={false}
                      stroke="#333"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#666", fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#666", fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip currency={currency} />} />
                    <Area
                      type="monotone"
                      dataKey="totalTables"
                      stroke={COLORS.cyan}
                      strokeWidth={2.5}
                      fill="url(#gradTableCyan)"
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="generatedRevenue"
                      stroke={COLORS.emerald}
                      strokeWidth={2.5}
                      fill="url(#gradTableGreen)"
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="avgTableRevenue"
                      stroke={COLORS.purple}
                      strokeWidth={2.5}
                      fill="url(#gradTablePurple)"
                      dot={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="guestsServed"
                      stroke={COLORS.royalBlue}
                      strokeWidth={2.5}
                      fill="url(#gradTableBlue)"
                      dot={false}
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-gray-400 text-sm mb-1 font-medium">
                Total Revenue
              </p>
              {isInitialLoading && !reportData ? (
                <Skeleton className="h-10 w-32" />
              ) : (
                <p className="text-3xl font-bold text-[#FBBF24]">
                  {formatCurrency(totalRevenueFromFilteredData, currency)}
                </p>
              )}
            </div>
            <div className="flex gap-4 items-center">
              {totalRevenueRange === "monthly" && (
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={totalMonth}
                    onChange={(e) =>
                      handleTotalMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              )}
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                  value={totalYear}
                  onChange={(e) =>
                    handleTotalYearChange(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
              <DashboardRangeTabs
                options={[
                  { label: "Monthly", value: "monthly" },
                  { label: "Yearly", value: "yearly" },
                ]}
                value={totalRevenueRange}
                onChange={handleTotalRangeChange}
                shape="full"
                className="border-white/10"
                buttonClassName="px-4 md:px-5 py-1.5 text-xs"
              />
            </div>
          </div>
          <div className="h-[380px] w-full mt-10">
            {isTotalLoading ? (
              <ChartSkeleton height={380} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={totalRevenueData}>
                  <CartesianGrid
                    vertical={false}
                    stroke="#333"
                    strokeDasharray="3 3"
                    opacity={0.3}
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 13 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.02)" }}
                    content={<CustomTooltip currency={currency} />}
                  />
                  <Bar
                    dataKey="revenue"
                    fill={COLORS.purple}
                    radius={[2, 2, 0, 0]}
                    barSize={12}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-lg font-medium">Revenue Comparison Yearly</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] border border-white/10 rounded px-8 py-1.5 text-xs text-gray-300 appearance-none"
                  value={compareYear1}
                  onChange={(e) =>
                    handleCompareYear1Change(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={`compare-year-1-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={12}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
              <span className="text-xs text-gray-600">Vs</span>
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] border border-white/10 rounded px-8 py-1.5 text-xs text-gray-300 appearance-none"
                  value={compareYear2}
                  onChange={(e) =>
                    handleCompareYear2Change(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={`compare-year-2-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={12}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
            </div>
          </div>
          <div className="h-[400px] w-full">
            {isYearlyLoading ? (
              <ChartSkeleton height={400} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={yearlyComparisonData}>
                  <CartesianGrid
                    vertical={true}
                    horizontal={false}
                    stroke="#333"
                    strokeDasharray="3 3"
                    opacity={0.3}
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#666", fontSize: 12 }}
                    dy={15}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#666", fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip currency={currency} />} />
                  {yearlyKeys.map((key, index) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={YEAR_COLORS[index % YEAR_COLORS.length]}
                      strokeWidth={3}
                      fill="transparent"
                      dot={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="flex justify-center gap-6 mt-6 text-xs text-gray-400">
            {yearlyKeys.map((key, index) => (
              <span key={key} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor: YEAR_COLORS[index % YEAR_COLORS.length],
                  }}
                />
                {key.replace("v", "")}
              </span>
            ))}
          </div>
        </section>
      </div>
    </Layout2>
  );
}

export default TableReports;
